import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Accordion, Table } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useAuthorization } from '../../../services/hooks';
import CustomFieldsInfo from './CustomFieldsInfo';
import FamilyGroupInfo from './FamilyGroupInfo';
import { debounceIndexShiftsRequest } from '../../../requests/shifts';
import Icon from '../../Icons';

const yesNo = value => {
  return value ? 'Sí' : 'No';
};

const BalanceShow = ({ balance }) => {
  return (
    <div className="info">
      <span className="type">{balance.balanceName}:</span>
      <span className="line" />
      <span className="answer">{balance.parsedAmount || 'No Aplica'}</span>
    </div>
  );
};

const ContractInfoContent = ({ admin, contract, contractCustomFields, employeePrevisionAttributes }) => {
  const [activeKey, setKey] = useState('info');
  const { translatedIneCode } = employeePrevisionAttributes;
  const {
    additionalHolidays,
    additionalHolidaysStartDate,
    administrativeDays,
    administrativeStartDate,
    acumulativeAdditionalHolidays,
    acumulativeAdministrative,
    agreedDays,
    bank,
    bankAccount,
    branchOffices,
    subsidiary,
    contractBalancesAttributes,
    contractBalancesRetentionsAttributes,
    contractCostCentersAttributes = [],
    correlativeIdentification,
    city,
    disability,
    disabilityFileInfo,
    disabilityNationalIdentification,
    extremeZoneAssignment,
    extremeZoneVacationBenefit,
    exceptionalSystemDate,
    documentTemplate,
    endDate,
    familyGroupsAttributes,
    fullAddressCompleted,
    jacketSize,
    jobManagement,
    jobTitle,
    journeyDistribution,
    markingTypes,
    outSourced,
    outSourceBusinessName,
    outSourceEstablishmentName,
    outSourceExceptionalSystem,
    outSourceExceptionalSystemNumber,
    outSourceNationalIdentification,
    pantsSize,
    parent,
    parsedSalary,
    presenceCheckAssistance,
    progressiveFileInfo,
    privateRole,
    recognizedMonths,
    recognizedMonthsFrom,
    shirtSize,
    shoesSize,
    startDate,
    status,
    syndicate,
    telecommuting,
    vacationCalculationArticle67,
    workScheduleArt27,
    translatedBankAccountType,
    translatedContractType,
    translatedForeignTechnician,
    translatedGratificationType,
    translatedIncomeTaxType,
    translatedPaymentSchedule,
    translatedPaymentType,
    translatedWorkSchedule,
    translatedWorkerQuality,
    tShirtSize,
    weeklyHours,
    workEndTime,
    workStartTime,
    dailyWorkingHours,
    supervisor,
    rima,
    rimaDate
  } = contract;

  const branchOfficeSummary = branchOffices.map(branchOffice => branchOffice.label);
  const constCenterSummary = contractCostCentersAttributes.map(cc => `${cc.name} (${parseInt(cc.percentage, 10)}%)`);
  const assets = [];
  const discounts = [];
  const balances = contractBalancesAttributes || [];
  const assetsData = balances.filter(b => b.balanceType === 'asset');
  const discountsData = balances.filter(b => b.balanceType !== 'asset');
  const dispatch = useDispatch();
  const [shiftSummary, setShiftSummary] = useState([]);

  if (contractBalancesAttributes) {
    contractBalancesAttributes.forEach((balance, index) => {
      if (balance.balanceType === 'asset') {
        assets.push(<BalanceShow key={`balance_${index.toString()}`} balance={balance} />);
        return;
      }
      discounts.push(<BalanceShow key={`balance_${index.toString()}`} balance={balance} />);
    });
  }
  const hasAdditionalInfo =
    (disability && disability !== 'no_disability' && disabilityNationalIdentification) ||
    (disability && disability !== 'no_disability' && disabilityFileInfo?.fileUrl) ||
    (outSourced && outSourceNationalIdentification) ||
    (outSourced && outSourceBusinessName) ||
    (outSourced && fullAddressCompleted) ||
    (outSourced && outSourceEstablishmentName) ||
    (outSourceExceptionalSystem && outSourceExceptionalSystemNumber);

  const canReadAsset = useAuthorization('read_asset', 'ContractBalance');
  const canReadDiscount = useAuthorization('read_discount', 'ContractBalance');
  const fetchActiveContractShift = () => {
    debounceIndexShiftsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 40,
        filter_active_contract_shifts: contract.id
      },
      successCallback: response => {
        const shifts = response.data.data;
        setShiftSummary(shifts.map(shift => shift.label));
      }
    });
  };

  useEffect(fetchActiveContractShift, []);

  return (
    <>
      {contract.status === 'finished' && (
        <Card className="mt-3 mr-2">
          <Card.Body>
            <Card.Title>Término de Contrato</Card.Title>
            <div className="info">
              <span className="type">Fecha de término:</span>
              <span className="line" />
              <span className={`answer ${!contract.terminationDate ? 'text-grey' : ''}`}>
                {contract.terminationDate || 'No Aplica'}
              </span>
            </div>
            <div className="info">
              <span className="type">Razón de término:</span>
              <span className="line" />
              <span className={`answer ${!contract.translatedTerminationReason ? 'text-grey' : ''}`}>
                {contract.translatedTerminationReason || 'No Aplica'}
              </span>
            </div>
            {contract.noticeDate && (
              <div className="info">
                <span className="type">Fecha de aviso:</span>
                <span className="line" />
                <span className={`answer ${!contract.noticeDate ? 'text-grey' : ''}`}>
                  {contract.noticeDate || 'No Aplica'}
                </span>
              </div>
            )}
            {contract.paymentDate && (
              <div className="info">
                <span className="type">Fecha de pago:</span>
                <span className="line" />
                <span className={`answer ${!contract.paymentDate ? 'text-grey' : ''}`}>
                  {contract.paymentDate || 'No Aplica'}
                </span>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
      <Card className="mb-3 mr-2 mt-4">
        <Col className={`${activeKey === 'info' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
          <Accordion.Toggle
            as={Col}
            eventKey="0"
            className="card-header-title"
            onClick={() => setKey('info')}
            style={{ textAlign: 'right' }}
          >
            Contrato {status === 'active' ? 'Vigente' : 'Finiquitado'} N°{correlativeIdentification}
          </Accordion.Toggle>
        </Col>
        <Accordion.Collapse eventKey="0">
          <Card.Body className="personal-data">
            <Row className="mt-3">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Área:</span>
                  <span className={`answer ${!jobManagement?.label ? 'text-grey' : ''}`}>
                    {jobManagement?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Lugar de Prestación de Servicio:</span>
                  <span className={`answer ${branchOfficeSummary.length === 0 ? 'text-grey' : ''}`}>
                    {branchOfficeSummary.length > 0 ? branchOfficeSummary.join(', ') : 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Sucursal:</span>
                  <span className={`answer ${!subsidiary?.label ? 'text-grey' : ''}`}>
                    {subsidiary?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Centro de Costo (Distribución):</span>
                  <span className={`answer ${!constCenterSummary.length ? 'text-grey' : ''}`}>
                    {constCenterSummary.join(', ') || 'No Aplica'}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Cargo:</span>
                  <span className={`answer ${!jobTitle?.label ? 'text-grey' : ''}`}>
                    {jobTitle?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Contrato:</span>
                  <span className={`answer ${!translatedContractType ? 'text-grey' : ''}`}>
                    {translatedContractType || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Fecha de Inicio:</span>
                  <span className={`answer ${!startDate ? 'text-grey' : ''}`}>{startDate || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Fecha de Término:</span>
                  <span className={`answer ${!endDate ? 'text-grey' : ''}`}>{endDate || 'No Aplica'}</span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Jefe Directo:</span>
                  <span className={`answer ${!parent?.label ? 'text-grey' : ''}`}>{parent?.label || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Calidad del Trabajador:</span>
                  <span className={`answer ${!translatedWorkerQuality ? 'text-grey' : ''}`}>
                    {translatedWorkerQuality || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Sindicato:</span>
                  <span className={`answer ${!syndicate?.label ? 'text-grey' : ''}`}>
                    {syndicate?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Plantilla Contrato:</span>
                  <span className={`answer ${!documentTemplate?.label ? 'text-grey' : ''}`}>
                    {documentTemplate?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Supervisor:</span>
                  <span className={`answer ${!supervisor?.label ? 'text-grey' : ''}`}>
                    {supervisor?.label || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Código INE:</span>
                  <span className={`answer ${!translatedIneCode ? 'text-grey' : ''}`}>
                    {translatedIneCode || 'No Aplica'}
                  </span>
                </div>
              </Col>

              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Ciudad Zona Extrema:</span>
                  <span className={`answer ${!city?.name ? 'text-grey' : ''}`}>{city?.name || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Asignación Zona Extrema(%):</span>
                  <span className={`answer ${!extremeZoneAssignment ? 'text-grey' : ''}`}>
                    {extremeZoneAssignment || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Vacaciones Zona Extrema:</span>
                  <span className={`answer ${extremeZoneVacationBenefit == null ? 'text-grey' : ''}`}>
                    {yesNo(extremeZoneVacationBenefit)}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Rol Privado:</span>
                  <span className={`answer ${privateRole == null ? 'text-grey' : ''}`}>{yesNo(privateRole)}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Subcontratado:</span>
                  <span className={`answer ${outSourced == null ? 'text-grey' : ''}`}>{yesNo(outSourced)}</span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2" />
            {(translatedForeignTechnician || (disability && disability !== 'no_disability') || outSourced) && (
              <Row className="row mt-3">
                <Col md={12} className="col-md-12">
                  <div className="d-flex flex-column mb-3">
                    <div className="d-flex flex-column mb-3">
                      {hasAdditionalInfo && <span className="answer mb-3">INFORMACIÓN ADICIONAL:</span>}
                      {disability && disability !== 'no_disability' && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Número de Credencial de Discapacidad:</span>
                          <span className={`answer ${!disabilityNationalIdentification ? 'text-grey' : ''}`}>
                            {disabilityNationalIdentification || 'No Aplica'}
                          </span>
                        </div>
                      )}
                      {disability && disability !== 'no_disability' && disabilityFileInfo && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Certificado de Discapacidad:</span>
                          <span className={`answer ${!disabilityFileInfo?.fileUrl ? 'text-grey' : ''}`}>
                            <a
                              target="_blank"
                              className="font-weight-bold underline"
                              rel="noopener noreferrer"
                              href={disabilityFileInfo?.fileUrl || ''}
                            >
                              {disabilityFileInfo?.filename || 'No Aplica'}
                            </a>
                          </span>
                        </div>
                      )}
                      {outSourced && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Rut de la empresa:</span>
                          <span className={`answer ${!outSourceNationalIdentification ? 'text-grey' : ''}`}>
                            {outSourceNationalIdentification || 'No Aplica'}
                          </span>
                        </div>
                      )}
                      {outSourced && outSourceBusinessName && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Razón social de la empresa:</span>
                          <span className={`answer ${!outSourceBusinessName ? 'text-grey' : ''}`}>
                            {outSourceBusinessName || 'No Aplica'}
                          </span>
                        </div>
                      )}
                      {outSourced && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Dirección:</span>
                          <span className={`answer ${!fullAddressCompleted ? 'text-grey' : ''}`}>
                            {fullAddressCompleted || 'No Aplica'}
                          </span>
                        </div>
                      )}
                      {outSourced && outSourceEstablishmentName && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Nombre de establecimiento:</span>
                          <span className={`answer ${!outSourceEstablishmentName ? 'text-grey' : ''}`}>
                            {outSourceEstablishmentName || 'No Aplica'}
                          </span>
                        </div>
                      )}
                      {outSourceExceptionalSystem && (
                        <div className="d-flex flex-column mb-3">
                          <span className="type">Número de sistema excepcional:</span>
                          <span className={`answer ${!outSourceExceptionalSystemNumber ? 'text-grey' : ''}`}>
                            {outSourceExceptionalSystemNumber || 'No Aplica'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      <Card className="mr-2 mb-3">
        <Col className={`${activeKey === 'journey' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
          <Accordion.Toggle as={Col} eventKey="1" className="card-header-title" onClick={() => setKey('journey')}>
            Jornada
          </Accordion.Toggle>
        </Col>
        <Accordion.Collapse eventKey="1">
          <Card.Body className="personal-data">
            <Row className="mt-3">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Jornada:</span>
                  <span className={`answer ${!translatedWorkSchedule ? 'text-grey' : ''}`}>
                    {translatedWorkSchedule || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Horas Semanales:</span>
                  <span className={`answer ${!weeklyHours ? 'text-grey' : ''}`}>{weeklyHours || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Horas Trabajadas al Día:</span>
                  <span className={`answer ${!dailyWorkingHours ? 'text-grey' : ''}`}>
                    {dailyWorkingHours || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Días Pactados Part-Time:</span>
                  <span className={`answer ${!agreedDays ? 'text-grey' : ''}`}>{agreedDays || 'No Aplica'}</span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Distribución de Jornada:</span>
                  <span className={`answer ${!journeyDistribution ? 'text-grey' : ''}`}>
                    {journeyDistribution || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Hora de Entrada:</span>
                  <span className={`answer ${!workStartTime ? 'text-grey' : ''}`}>{workStartTime || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Hora de Salida:</span>
                  <span className={`answer ${!workEndTime ? 'text-grey' : ''}`}>{workEndTime || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">N° Jornada Excepcional:</span>
                  <span className={`answer ${!outSourceExceptionalSystemNumber ? 'text-grey' : ''}`}>
                    {outSourceExceptionalSystemNumber || 'No Aplica'}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Emisión Jornada Excepcional:</span>
                  <span className={`answer ${!exceptionalSystemDate ? 'text-grey' : ''}`}>
                    {exceptionalSystemDate || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Cálculo Vacaciones Art 67:</span>
                  <span className={`answer ${vacationCalculationArticle67 == null ? 'text-grey' : ''}`}>
                    {yesNo(vacationCalculationArticle67)}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Banda Horaria Art. 27:</span>
                  <span className={`answer ${workScheduleArt27 == null ? 'text-grey' : ''}`}>
                    {yesNo(workScheduleArt27)}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Teletrabajo:</span>
                  <span className={`answer ${telecommuting == null ? 'text-grey' : ''}`}>{yesNo(telecommuting)}</span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Marca Asistencia:</span>
                  <span className={`answer ${presenceCheckAssistance == null ? 'text-grey' : ''}`}>
                    {yesNo(presenceCheckAssistance)}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Marca Presencial:</span>
                  <span className={`answer ${presenceCheckAssistance == null ? 'text-grey' : ''}`}>
                    {yesNo(presenceCheckAssistance)}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Marcaje:</span>
                  <span className={`answer ${!markingTypes?.length ? 'text-grey' : ''}`}>
                    {markingTypes && markingTypes.length > 0
                      ? markingTypes.map(type => type.label || 'No Aplica').join(', ')
                      : 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Turno Asignado:</span>
                  <span className={`answer ${!shiftSummary ? 'text-grey' : ''}`}>
                    {shiftSummary?.length ? shiftSummary.join(', ') : 'No Aplica'}
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      <Card className="mr-2 mb-3">
        <Col className={`${activeKey === 'benefits' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
          <Accordion.Toggle as={Col} eventKey="2" className="card-header-title" onClick={() => setKey('benefits')}>
            Beneficios Contractuales
          </Accordion.Toggle>
        </Col>
        <Accordion.Collapse eventKey="2">
          <Card.Body className="personal-data">
            <Row className="mt-3">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Días de Vacaciones Adicionales:</span>
                  <span className={`answer ${!additionalHolidays ? 'text-grey' : ''}`}>
                    {additionalHolidays || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Válida desde:</span>
                  <span className={`answer ${!additionalHolidaysStartDate ? 'text-grey' : ''}`}>
                    {additionalHolidaysStartDate || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Beneficio Acumulable:</span>
                  <span className={`answer ${acumulativeAdditionalHolidays == null ? 'text-grey' : ''}`}>
                    {yesNo(acumulativeAdditionalHolidays)}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Días Administrativos:</span>
                  <span className={`answer ${!administrativeDays ? 'text-grey' : ''}`}>
                    {administrativeDays || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Válido desde:</span>
                  <span className={`answer ${!administrativeStartDate ? 'text-grey' : ''}`}>
                    {administrativeStartDate || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Beneficio Acumulable:</span>
                  <span className={`answer ${acumulativeAdministrative == null ? 'text-grey' : ''}`}>
                    {yesNo(acumulativeAdministrative)}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Meses Reconocido Vac. Progresivas:</span>
                  <span className={`answer ${!recognizedMonths ? 'text-grey' : ''}`}>
                    {recognizedMonths || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Válido desde:</span>
                  <span className={`answer ${!recognizedMonthsFrom ? 'text-grey' : ''}`}>
                    {recognizedMonthsFrom || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex align-items-center h-100">
                  {progressiveFileInfo?.fileUrl && (
                    <a
                      href={progressiveFileInfo.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={progressiveFileInfo.cleanFilename}
                      title={`Descargar: ${progressiveFileInfo.cleanFilename}`}
                      className="download-icon"
                    >
                      <Icon icon="download-2" height="16px" width="16px" />
                    </a>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      <Card className="mr-2 mb-3">
        <Col className={`${activeKey === 'salary' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
          <Accordion.Toggle
            as={Col}
            eventKey="3"
            className="card-header-title"
            onClick={() => setKey('salary')}
            style={{ textAlign: 'right' }}
          >
            Sueldo, Haberes y Descuentos Contractuales
          </Accordion.Toggle>
        </Col>
        <Accordion.Collapse eventKey="3">
          <Card.Body className="personal-data">
            <Row className="mt-3">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Sueldo Base:</span>
                  <span className={`answer ${!parsedSalary ? 'text-grey' : ''}`}>{parsedSalary || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Pago:</span>
                  <span className={`answer ${!translatedPaymentSchedule ? 'text-grey' : ''}`}>
                    {translatedPaymentSchedule || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Forma de Pago:</span>
                  <span className={`answer ${!translatedPaymentType ? 'text-grey' : ''}`}>
                    {translatedPaymentType || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Banco de Pago:</span>
                  <span className={`answer ${!bank?.label ? 'text-grey' : ''}`}>{bank?.label || 'No Aplica'}</span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">N° Cuenta Depósito:</span>
                  <span className={`answer ${!bankAccount ? 'text-grey' : ''}`}>{bankAccount || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Cuenta:</span>
                  <span className={`answer ${!translatedBankAccountType ? 'text-grey' : ''}`}>
                    {translatedBankAccountType || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Gratificación:</span>
                  <span className={`answer ${!translatedGratificationType ? 'text-grey' : ''}`}>
                    {translatedGratificationType || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Régimen de Gratificación:</span>
                  <span className={`answer ${!translatedGratificationType ? 'text-grey' : ''}`}>
                    {translatedGratificationType || 'No Aplica'}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Tipo de Impuesto Renta:</span>
                  <span className={`answer ${!translatedIncomeTaxType ? 'text-grey' : ''}`}>
                    {translatedIncomeTaxType || 'No Aplica'}
                  </span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">RIMA:</span>
                  <span className={`answer ${!rima ? 'text-grey' : ''}`}>{rima || 'No Aplica'}</span>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Fecha de RIMA:</span>
                  <span className={`answer ${!rimaDate ? 'text-grey' : ''}`}>{rimaDate || 'No Aplica'}</span>
                </div>
              </Col>
            </Row>
            {canReadAsset && (
              <Row className="mt-4">
                <Col xs={12}>
                  <span className="type d-block mb-2">Haberes:</span>
                  <Table striped bordered className="yellow-table mx-auto">
                    <colgroup>
                      <col style={{ width: '40%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '12.5%' }} />
                      <col style={{ width: '12.5%' }} />
                    </colgroup>
                    <thead className="bg-light">
                      <tr>
                        <th>Haber</th>
                        <th>Moneda</th>
                        <th>Monto</th>
                        <th>Desde</th>
                        <th>Hasta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetsData.length > 0 ? (
                        assetsData.map(h => (
                          <tr key={h.id}>
                            <td>{h.balanceName}</td>
                            <td>{h.parsedUnitOfAccount}</td>
                            <td>{h.parsedAmount}</td>
                            <td>{h.startDate}</td>
                            <td>{h.endDate || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            Sin haberes
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            )}
            {canReadDiscount && (
              <Row className="mt-4 mb-4">
                <Col xs={12}>
                  <span className="type d-block mb-2">Descuentos:</span>
                  <Table striped bordered className="yellow-table mx-auto">
                    <colgroup>
                      <col style={{ width: '40%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '12.5%' }} />
                      <col style={{ width: '12.5%' }} />
                    </colgroup>
                    <thead className="bg-light">
                      <tr>
                        <th>Descuento</th>
                        <th>Moneda</th>
                        <th>Monto</th>
                        <th>Desde</th>
                        <th>Hasta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discountsData.length > 0 ? (
                        discountsData.map(d => (
                          <tr key={d.id}>
                            <td>{d.balanceName}</td>
                            <td>{d.parsedUnitOfAccount}</td>
                            <td>{d.parsedAmount}</td>
                            <td>{d.startDate}</td>
                            <td>{d.endDate || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            Sin descuentos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            )}
            <Row className="mt-2">
              <Col xs={12}>
                <span className="type d-block mb-4">Retenciones :</span>
              </Col>
            </Row>
            {contractBalancesRetentionsAttributes.map(retention => (
              <Row key={retention.id} className="mt-2">
                <Col xs={12} md={3}>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Retención:</span>
                    <span className={`answer ${!retention.balanceName ? 'text-grey' : ''}`}>
                      {retention.balanceName || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">N° de Expediente:</span>
                    <span className={`answer ${!retention.contractRetention?.fileNumber ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.fileNumber || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Rut Beneficiario:</span>
                    <span
                      className={`answer ${
                        !retention.contractRetention?.nationalIdentificationBeneficiary ? 'text-grey' : ''
                      }`}
                    >
                      {retention.contractRetention?.nationalIdentificationBeneficiary || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Tipo de Cuenta:</span>
                    <span
                      className={`answer ${!retention.contractRetention?.translatedBankAccountType ? 'text-grey' : ''}`}
                    >
                      {retention.contractRetention?.translatedBankAccountType || 'No Aplica'}
                    </span>
                  </div>
                </Col>

                <Col xs={12} md={3}>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Moneda:</span>
                    <span className={`answer ${!retention.unitOfAccount ? 'text-grey' : ''}`}>
                      {retention.unitOfAccount || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Fecha de Retención:</span>
                    <span className={`answer ${!retention.contractRetention?.retentionDate ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.retentionDate || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Forma de Pago:</span>
                    <span
                      className={`answer ${!retention.contractRetention?.translatedPaymentType ? 'text-grey' : ''}`}
                    >
                      {retention.contractRetention?.translatedPaymentType || 'No Aplica'}
                    </span>
                  </div>
                </Col>

                <Col xs={12} md={3}>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Monto:</span>
                    <span className={`answer ${!retention.parsedAmount ? 'text-grey' : ''}`}>
                      {retention.parsedAmount || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Tribunal Competente:</span>
                    <span className={`answer ${!retention.contractRetention?.competentCourt ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.competentCourt || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Banco de Pago:</span>
                    <span className={`answer ${!retention.contractRetention?.bank?.label ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.bank?.label || 'No Aplica'}
                    </span>
                  </div>
                </Col>

                <Col xs={12} md={3}>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Desde - Hasta:</span>
                    <span className={`answer ${!retention.startDate ? 'text-grey' : ''}`}>
                      {`${retention.startDate || '??'} - ${retention.endDate || '??'}`}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">Nombre Beneficiario:</span>
                    <span className={`answer ${!retention.contractRetention?.beneficiary ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.beneficiary || 'No Aplica'}
                    </span>
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <span className="type">N° Cuenta de Pago:</span>
                    <span className={`answer ${!retention.contractRetention?.bankAccount ? 'text-grey' : ''}`}>
                      {retention.contractRetention?.bankAccount || 'No Aplica'}
                    </span>
                  </div>
                </Col>
              </Row>
            ))}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      <>
        {(useAuthorization('read', 'FamilyGroup') || admin) && (
          <FamilyGroupInfo
            activeKey={activeKey}
            activeAccordion={() => setKey('family-group')}
            familyGroupsAttributes={familyGroupsAttributes}
          />
        )}
      </>
      <Card className="mr-2 mb-3">
        <Col className={`${activeKey === 'sizes' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
          <Accordion.Toggle as={Col} eventKey="5" className="card-header-title" onClick={() => setKey('sizes')}>
            Otros
          </Accordion.Toggle>
        </Col>
        <Accordion.Collapse eventKey="5">
          <Card.Body className="personal-data">
            <Row className="mt-3">
              <Col md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Talla Pantalón:</span>
                  <span className="answer">{pantsSize || 'No Aplica'}</span>
                </div>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Talla Zapatos:</span>
                  <span className="answer">{shoesSize || 'No Aplica'}</span>
                </div>
              </Col>

              <Col md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Talla Camisa:</span>
                  <span className="answer">{shirtSize || 'No Aplica'}</span>
                </div>
              </Col>

              <Col md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Talla Polera:</span>
                  <span className="answer">{tShirtSize || 'No Aplica'}</span>
                </div>
              </Col>

              <Col md={3}>
                <div className="d-flex flex-column mb-3">
                  <span className="type">Talla Chaqueta:</span>
                  <span className="answer">{jacketSize || 'No Aplica'}</span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      <>
        {contractCustomFields && contractCustomFields.length > 0 && (
          <CustomFieldsInfo
            extraFieldsAttributes={contract.extraFieldsAttributes}
            customFields={contractCustomFields}
            contract={contract}
            activeAccordion={() => setKey('custom-fields')}
            activeKey={activeKey}
          />
        )}
      </>
    </>
  );
};

export default ContractInfoContent;
